const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

function withTimeout(promise, timeoutMs) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Branding fetch timed out")), timeoutMs)
    ),
  ]);
}

// Converts "#1E3A5F" -> "213 51% 23%" (raw HSL triplet, matching index.css's
// CSS custom property convention of hsl(var(--x)) with no hsl()/commas).
export function hexToHslTriplet(hex) {
  if (!hex || typeof hex !== "string") return null;
  const clean = hex.replace("#", "").trim();
  const full = clean.length === 3
    ? clean.split("").map((c) => c + c).join("")
    : clean;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Resolves a partner's branding (name/logo) + next-widget theme via the
// backend (which proxies the Sanity lookup server-side — see
// backend/server.py's /api/branding/{partner_key}, added so the widget
// doesn't need Sanity's CORS origins to cover every dealer domain it's
// embedded on). Never throws — any failure (missing key, not found, network
// error, timeout) resolves to null so callers can safely fall back to defaults.
//
// Memoized per partner key for the life of the page, so a page with several
// widget instances (eg. bar + inline embed on a VDP) makes one request, not
// one per mount.
const brandingRequests = new Map();

export function resolvePartnerBranding(partnerKey, options) {
  if (!partnerKey) return Promise.resolve(null);
  if (!brandingRequests.has(partnerKey)) {
    const request = fetchPartnerBranding(partnerKey, options);
    brandingRequests.set(partnerKey, request);
    // Don't pin a failure for the rest of the page's life — let a later mount retry.
    request.then((data) => { if (!data) brandingRequests.delete(partnerKey); });
  }
  return brandingRequests.get(partnerKey);
}

async function fetchPartnerBranding(partnerKey, { timeoutMs = 4000 } = {}) {
  try {
    const res = await withTimeout(
      fetch(`${BACKEND_URL}/api/branding/${encodeURIComponent(partnerKey)}`),
      timeoutMs
    );
    if (!res.ok) return null;

    const data = await res.json();
    if (!data || (!data.name && !data.logoUrl && !data.footerLogoUrl && !data.theme && !data.phone)) {
      return null;
    }
    return data;
  } catch (e) {
    console.warn(`[nucar-next] Failed to resolve branding for partner "${partnerKey}":`, e.message);
    return null;
  }
}
